import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';

const { 
  FiTrophy, FiStar, FiAward, FiZap, FiFileText, FiUsers, FiSettings, 
  FiToggleLeft, FiToggleRight, FiUser, FiEye, FiEyeOff, FiArrowUp, FiCalendar,
  FiCoffee, FiCheck, FiGift, FiThumbsUp, FiFire, FiUpload
} = FiIcons;

const BADGE_ICONS = {
  FiTrophy,
  FiStar,
  FiAward,
  FiZap,
  FiFileText,
  FiCalendar,
  FiCoffee,
  FiCheck,
  FiGift,
  FiThumbsUp,
  FiFire,
  FiUpload
};

const Scoreboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [userBadges, setUserBadges] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [showOnLeaderboard, setShowOnLeaderboard] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pointsSystem, setPointsSystem] = useState({});
  const [badgeDefinitions, setBadgeDefinitions] = useState({});
  const [showLoginAnimation, setShowLoginAnimation] = useState(false);
  const [loginPoints, setLoginPoints] = useState(0);

  useEffect(() => {
    if (user) {
      loadData();
      // Get points system and badge definitions
      setPointsSystem(userService.getPointsSystem());
      setBadgeDefinitions(userService.getBadgeDefinitions());
    }
  }, [user]);
  
  // Effect to award login points once
  useEffect(() => {
    if (user && userStats) {
      const handleLoginPoints = async () => {
        // Check if login points have been awarded today
        const loginResult = await userService.awardLoginPoints(user.id);
        if (loginResult.awarded) {
          setLoginPoints(loginResult.points);
          setShowLoginAnimation(true);
          
          // Refresh data after awarding points
          setTimeout(() => {
            loadData();
            setShowLoginAnimation(false);
          }, 3000);
          
          // Add to recent activity
          addRecentActivity({
            type: 'login',
            points: loginResult.points,
            message: `Daily Login (Day ${loginResult.currentStreak})`,
            icon: FiCalendar,
            color: 'bg-blue-500'
          });
        }
      };
      
      handleLoginPoints();
    }
  }, [user, userStats]);

  // Load recent activity from local storage
  useEffect(() => {
    if (user) {
      const storedActivity = localStorage.getItem(`${user.id}_recent_activity`);
      if (storedActivity) {
        try {
          setRecentActivity(JSON.parse(storedActivity));
        } catch (e) {
          console.error('Failed to parse stored activity:', e);
        }
      }
    }
  }, [user]);
  
  // Function to add activity to recent activity list
  const addRecentActivity = (activity) => {
    const newActivity = {
      ...activity,
      id: `activity_${Date.now()}`,
      timestamp: new Date().toISOString()
    };
    
    const updatedActivity = [newActivity, ...recentActivity].slice(0, 10);
    setRecentActivity(updatedActivity);
    
    // Store in local storage
    if (user) {
      localStorage.setItem(`${user.id}_recent_activity`, JSON.stringify(updatedActivity));
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load leaderboard data
      const leaderboardData = await userService.getLeaderboard();
      
      // If user exists in the data, ensure it's accurate
      const userIndex = leaderboardData.findIndex(entry => entry.user_id === user.id);
      
      // Load user stats
      const stats = await userService.getUserStats(user.id);
      setUserStats(stats);
      setShowOnLeaderboard(stats.show_on_leaderboard);
      
      // If user is in leaderboard, update their data
      if (userIndex !== -1) {
        leaderboardData[userIndex] = {
          ...leaderboardData[userIndex],
          questions_created: stats.questions_created,
          exams_generated: stats.exams_generated,
          total_points: stats.total_points,
          streak_days: stats.streak_days
        };
      } else if (stats.show_on_leaderboard) {
        // Add user to leaderboard if they should be visible
        leaderboardData.push({
          id: `user-${user.id}`,
          user_id: user.id,
          questions_created: stats.questions_created,
          exams_generated: stats.exams_generated,
          total_points: stats.total_points,
          streak_days: stats.streak_days,
          profiles: {
            full_name: user.name,
            avatar_url: user.avatar
          }
        });
      }
      
      // Sort leaderboard by points
      leaderboardData.sort((a, b) => b.total_points - a.total_points);
      
      setLeaderboard(leaderboardData);
      
      // Find user's rank in leaderboard
      const rank = leaderboardData.findIndex(entry => entry.user_id === user.id);
      setUserRank(rank !== -1 ? rank + 1 : null);
      
      // Load user badges
      const badges = await userService.getUserBadges(user.id);
      setUserBadges(badges);
    } catch (error) {
      console.error('Error loading scoreboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLeaderboardVisibility = async () => {
    setIsSubmitting(true);
    try {
      await userService.toggleLeaderboardVisibility(user.id, !showOnLeaderboard);
      setShowOnLeaderboard(!showOnLeaderboard);
      
      // Reload data to reflect changes
      await loadData();
    } catch (error) {
      console.error('Error toggling leaderboard visibility:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBadgeIcon = (iconName) => {
    return BADGE_ICONS[iconName] || FiAward;
  };

  const getLeaderboardPosition = (position) => {
    if (position === 1) return "🥇 Champion";
    if (position === 2) return "🥈 Runner-up";
    if (position === 3) return "🥉 Third Place";
    if (position <= 10) return "🏆 Top 10";
    if (position <= 20) return "⭐ Top 20";
    return `#${position}`;
  };

  const getPositionColor = (position) => {
    if (position === 1) return "text-yellow-500";
    if (position === 2) return "text-gray-400";
    if (position === 3) return "text-amber-600";
    if (position <= 10) return "text-blue-600";
    return "text-gray-600";
  };
  
  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-8 ml-72">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading scoreboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 ml-72">
      {/* Login Animation */}
      <AnimatePresence>
        {showLoginAnimation && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.8, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            className="fixed top-24 right-24 z-50"
          >
            <div className="bg-blue-600 text-white px-6 py-4 rounded-xl shadow-xl flex items-center space-x-3">
              <div className="p-3 bg-blue-500 rounded-full">
                <SafeIcon icon={FiCalendar} className="text-xl" />
              </div>
              <div>
                <p className="font-semibold">Daily Login Bonus!</p>
                <div className="flex items-center">
                  <SafeIcon icon={FiStar} className="text-yellow-300 mr-1" />
                  <span className="font-bold">{loginPoints} points</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Scoreboard</h2>
          <p className="text-gray-600 mt-1">
            Compare your progress with other users and earn badges
          </p>
        </div>
      </div>

      {/* User Stats Card */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 shadow-xl">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="relative">
              <img 
                src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=3b82f6&color=fff`} 
                alt={user.name} 
                className="w-16 h-16 rounded-full border-4 border-white shadow-md"
              />
              {userRank && userRank <= 3 && (
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-white font-bold shadow-lg border-2 border-white">
                  {userRank}
                </div>
              )}
            </div>
            <div className="ml-4 text-white">
              <h3 className="text-xl font-bold">{user.name}</h3>
              <div className="flex items-center mt-1">
                {userRank ? (
                  <span className={`text-sm ${getPositionColor(userRank)}`}>
                    {getLeaderboardPosition(userRank)}
                  </span>
                ) : (
                  <span className="text-gray-200 text-sm">Not ranked yet</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 md:gap-8">
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                <div className="text-2xl font-bold text-white">{userStats?.total_points || 0}</div>
                <div className="text-xs text-gray-200">POINTS</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                <div className="text-2xl font-bold text-white">{userStats?.questions_created || 0}</div>
                <div className="text-xs text-gray-200">QUESTIONS</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                <div className="text-2xl font-bold text-white">{userBadges?.length || 0}</div>
                <div className="text-xs text-gray-200">BADGES</div>
              </div>
            </div>
          </div>
        </div>

        {/* User Badges */}
        {userBadges && userBadges.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/20">
            <div className="flex items-center mb-3">
              <SafeIcon icon={FiAward} className="text-yellow-300 mr-2" />
              <h4 className="text-white font-semibold">Your Badges</h4>
            </div>
            <div className="flex flex-wrap gap-3">
              {userBadges.map(badge => {
                const BadgeIcon = getBadgeIcon(badge.badges?.icon);
                return (
                  <div 
                    key={badge.id}
                    className="bg-white/20 backdrop-blur-sm rounded-lg p-3 flex items-center"
                    title={badge.badges?.description}
                  >
                    <div className={`w-8 h-8 rounded-full bg-${badge.badges?.color}-500 flex items-center justify-center text-white`}>
                      <SafeIcon icon={BadgeIcon} />
                    </div>
                    <span className="ml-2 text-white text-sm font-medium">{badge.badges?.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Recent Activity */}
        <div className="mt-6 pt-6 border-t border-white/20">
          <div className="flex items-center mb-3">
            <SafeIcon icon={FiFire} className="text-yellow-300 mr-2" />
            <h4 className="text-white font-semibold">Recent Activity</h4>
          </div>
          
          {recentActivity.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recentActivity.slice(0, 4).map(activity => (
                <div 
                  key={activity.id}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 flex items-center"
                >
                  <div className={`w-10 h-10 rounded-full ${activity.color || 'bg-blue-500'} flex items-center justify-center text-white`}>
                    <SafeIcon icon={activity.icon || FiStar} />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-white text-sm font-medium">{activity.message}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-white/70">{getRelativeTime(activity.timestamp)}</span>
                      <span className="text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full text-white flex items-center">
                        <SafeIcon icon={FiStar} className="text-yellow-300 mr-1" />
                        {activity.points} pts
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-white/70 py-3">
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm p-1 flex">
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center ${
            activeTab === 'leaderboard' 
              ? 'bg-primary-50 text-primary-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <SafeIcon icon={FiTrophy} className="mr-2" />
          Leaderboard
        </button>
        <button
          onClick={() => setActiveTab('badges')}
          className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center ${
            activeTab === 'badges' 
              ? 'bg-primary-50 text-primary-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <SafeIcon icon={FiAward} className="mr-2" />
          Available Badges
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center ${
            activeTab === 'activity' 
              ? 'bg-primary-50 text-primary-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <SafeIcon icon={FiFire} className="mr-2" />
          Activity
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center ${
            activeTab === 'settings' 
              ? 'bg-primary-50 text-primary-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <SafeIcon icon={FiSettings} className="mr-2" />
          Settings
        </button>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'leaderboard' && (
          <motion.div
            key="leaderboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-sm overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <SafeIcon icon={FiUsers} className="mr-2 text-primary-600" />
                Top Contributors
              </h3>
            </div>

            {leaderboard.length === 0 ? (
              <div className="p-8 text-center">
                <SafeIcon icon={FiUsers} className="mx-auto text-4xl text-gray-300 mb-4" />
                <p className="text-gray-500">No leaderboard data available yet</p>
                <p className="text-gray-400 text-sm mt-2">Start creating questions to appear on the leaderboard!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {leaderboard.map((entry, index) => (
                  <div 
                    key={entry.id || `leaderboard-${index}`} 
                    className={`p-4 flex items-center ${entry.user_id === user.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-gray-100 text-gray-700' :
                      index === 2 ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-50 text-blue-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="ml-4 flex-1 flex items-center">
                      <img 
                        src={entry.profiles?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.profiles?.full_name || 'User')}&background=3b82f6&color=fff`}
                        alt={entry.profiles?.full_name || 'User'} 
                        className="w-10 h-10 rounded-full"
                      />
                      <div className="ml-3">
                        <div className="font-medium text-gray-800">
                          {entry.profiles?.full_name || 'Anonymous User'}
                          {entry.user_id === user.id && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">You</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center space-x-2">
                          <span><SafeIcon icon={FiStar} className="inline mr-1" /> {entry.total_points} points</span>
                          <span>•</span>
                          <span><SafeIcon icon={FiFileText} className="inline mr-1" /> {entry.questions_created} questions</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-900">{getLeaderboardPosition(index + 1)}</div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center justify-end">
                        <SafeIcon icon={FiCalendar} className="mr-1" />
                        <span>{entry.streak_days} day streak</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!showOnLeaderboard && (
              <div className="p-4 bg-yellow-50 border-t border-yellow-100">
                <div className="flex items-center text-yellow-700">
                  <SafeIcon icon={FiEyeOff} className="mr-2" />
                  <p className="text-sm">You're not visible on the leaderboard. Change this in Settings.</p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'badges' && (
          <motion.div
            key="badges"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-sm overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <SafeIcon icon={FiAward} className="mr-2 text-primary-600" />
                Available Badges
              </h3>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Novice Badge */}
              <div className={`${
                userBadges.some(b => b.badges?.id === 'novice') 
                  ? 'border-blue-300 bg-blue-50' 
                  : 'border-gray-200 bg-gray-50'
              } border rounded-xl p-5 relative`}>
                {userBadges.some(b => b.badges?.id === 'novice') && (
                  <div className="absolute top-3 right-3 bg-blue-500 text-white p-1 rounded-full">
                    <SafeIcon icon={FiCheck} className="text-sm" />
                  </div>
                )}
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white">
                    <SafeIcon icon={FiStar} className="text-xl" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-800">Novice</h4>
                    <p className="text-sm text-gray-600">Just getting started</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-gray-600 mb-1">Requirements</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Join FAHIM</span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">0 points</span>
                  </div>
                </div>
              </div>

              {/* Contributor Badge */}
              <div className={`${
                userBadges.some(b => b.badges?.id === 'contributor') 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-gray-200 bg-gray-50'
              } border rounded-xl p-5 relative`}>
                {userBadges.some(b => b.badges?.id === 'contributor') && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white p-1 rounded-full">
                    <SafeIcon icon={FiCheck} className="text-sm" />
                  </div>
                )}
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white">
                    <SafeIcon icon={FiFileText} className="text-xl" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-800">Contributor</h4>
                    <p className="text-sm text-gray-600">Created at least 10 questions</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-gray-600 mb-1">Requirements</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Create 10 questions</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">100 points</span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500" 
                      style={{ width: `${Math.min(100, (userStats?.questions_created || 0) / 10 * 100)}%` }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-right text-gray-600">
                    {userStats?.questions_created || 0}/10 questions
                  </div>
                </div>
              </div>

              {/* Expert Badge */}
              <div className={`${
                userBadges.some(b => b.badges?.id === 'expert') 
                  ? 'border-purple-300 bg-purple-50' 
                  : 'border-gray-200 bg-gray-50'
              } border rounded-xl p-5 relative`}>
                {userBadges.some(b => b.badges?.id === 'expert') && (
                  <div className="absolute top-3 right-3 bg-purple-500 text-white p-1 rounded-full">
                    <SafeIcon icon={FiCheck} className="text-sm" />
                  </div>
                )}
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center text-white">
                    <SafeIcon icon={FiAward} className="text-xl" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-800">Expert</h4>
                    <p className="text-sm text-gray-600">Created at least 50 questions</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-gray-600 mb-1">Requirements</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Create 50 questions</span>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">500 points</span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500" 
                      style={{ width: `${Math.min(100, (userStats?.questions_created || 0) / 50 * 100)}%` }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-right text-gray-600">
                    {userStats?.questions_created || 0}/50 questions
                  </div>
                </div>
              </div>

              {/* Master Badge */}
              <div className={`${
                userBadges.some(b => b.badges?.id === 'master') 
                  ? 'border-orange-300 bg-orange-50' 
                  : 'border-gray-200 bg-gray-50'
              } border rounded-xl p-5 relative`}>
                {userBadges.some(b => b.badges?.id === 'master') && (
                  <div className="absolute top-3 right-3 bg-orange-500 text-white p-1 rounded-full">
                    <SafeIcon icon={FiCheck} className="text-sm" />
                  </div>
                )}
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white">
                    <SafeIcon icon={FiTrophy} className="text-xl" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-800">Master</h4>
                    <p className="text-sm text-gray-600">Created at least 100 questions</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-gray-600 mb-1">Requirements</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Create 100 questions</span>
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">1000 points</span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-orange-500" 
                      style={{ width: `${Math.min(100, (userStats?.questions_created || 0) / 100 * 100)}%` }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-right text-gray-600">
                    {userStats?.questions_created || 0}/100 questions
                  </div>
                </div>
              </div>

              {/* Guru Badge */}
              <div className={`${
                userBadges.some(b => b.badges?.id === 'guru') 
                  ? 'border-pink-300 bg-pink-50' 
                  : 'border-gray-200 bg-gray-50'
              } border rounded-xl p-5 relative`}>
                {userBadges.some(b => b.badges?.id === 'guru') && (
                  <div className="absolute top-3 right-3 bg-pink-500 text-white p-1 rounded-full">
                    <SafeIcon icon={FiCheck} className="text-sm" />
                  </div>
                )}
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-pink-500 flex items-center justify-center text-white">
                    <SafeIcon icon={FiZap} className="text-xl" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-800">Guru</h4>
                    <p className="text-sm text-gray-600">Created at least 500 questions</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-gray-600 mb-1">Requirements</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Create 500 questions</span>
                    <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full">5000 points</span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-pink-500" 
                      style={{ width: `${Math.min(100, (userStats?.questions_created || 0) / 500 * 100)}%` }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-right text-gray-600">
                    {userStats?.questions_created || 0}/500 questions
                  </div>
                </div>
              </div>
              
              {/* Streak Master Badge */}
              <div className={`${
                userBadges.some(b => b.badges?.id === 'streak_master') 
                  ? 'border-indigo-300 bg-indigo-50' 
                  : 'border-gray-200 bg-gray-50'
              } border rounded-xl p-5 relative`}>
                {userBadges.some(b => b.badges?.id === 'streak_master') && (
                  <div className="absolute top-3 right-3 bg-indigo-500 text-white p-1 rounded-full">
                    <SafeIcon icon={FiCheck} className="text-sm" />
                  </div>
                )}
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center text-white">
                    <SafeIcon icon={FiCalendar} className="text-xl" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-800">Streak Master</h4>
                    <p className="text-sm text-gray-600">Maintained a 7-day login streak</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-gray-600 mb-1">Requirements</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Login 7 days in a row</span>
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">200 points</span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-500" 
                      style={{ width: `${Math.min(100, (userStats?.streak_days || 0) / 7 * 100)}%` }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-right text-gray-600">
                    {userStats?.streak_days || 0}/7 days
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100">
              <h4 className="font-semibold text-gray-800 mb-2">How to Earn Points</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center">
                    <SafeIcon icon={FiFileText} className="text-green-600" />
                    <span className="ml-2 text-sm text-gray-700">Create a question: +{pointsSystem.CREATE_QUESTION || 10} points</span>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center">
                    <SafeIcon icon={FiZap} className="text-blue-600" />
                    <span className="ml-2 text-sm text-gray-700">Generate an exam: +{pointsSystem.GENERATE_EXAM || 20} points</span>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="text-purple-600" />
                    <span className="ml-2 text-sm text-gray-700">Daily login: +{pointsSystem.LOGIN || 5} points/day</span>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center">
                    <SafeIcon icon={FiUpload} className="text-orange-600" />
                    <span className="ml-2 text-sm text-gray-700">Bulk upload bonus: +{pointsSystem.BULK_UPLOAD_BONUS || 25} points</span>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center">
                    <SafeIcon icon={FiFire} className="text-red-600" />
                    <span className="ml-2 text-sm text-gray-700">Login streak: +{pointsSystem.LOGIN_STREAK_BONUS || 2} points/day extra</span>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center">
                    <SafeIcon icon={FiAward} className="text-indigo-600" />
                    <span className="ml-2 text-sm text-gray-700">Earn a badge: +{pointsSystem.MILESTONE_COMPLETION || 50} points</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
        
        {activeTab === 'activity' && (
          <motion.div
            key="activity"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-sm overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <SafeIcon icon={FiFire} className="mr-2 text-primary-600" />
                Your Activity
              </h3>
            </div>
            
            <div className="divide-y divide-gray-100">
              {recentActivity.length > 0 ? (
                recentActivity.map(activity => (
                  <div 
                    key={activity.id} 
                    className="p-4 hover:bg-gray-50"
                  >
                    <div className="flex items-center">
                      <div className={`p-3 rounded-full ${activity.color || 'bg-blue-500'} text-white`}>
                        <SafeIcon icon={activity.icon || FiStar} />
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-800">{activity.message}</h4>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            +{activity.points} points
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{getRelativeTime(activity.timestamp)}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center">
                  <SafeIcon icon={FiCalendar} className="mx-auto text-4xl text-gray-300 mb-4" />
                  <p className="text-gray-500">No activity recorded yet</p>
                  <p className="text-gray-400 text-sm mt-2">Start using FAHIM to earn points and track your progress!</p>
                </div>
              )}
            </div>
            
            <div className="p-6 bg-gray-50 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Your activity is tracked to help you earn points and badges. Login daily and create questions to maximize your points!
              </p>
            </div>
          </motion.div>
        )}

        {activeTab === 'settings' && (
          <motion.div
            key="settings"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-sm overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <SafeIcon icon={FiSettings} className="mr-2 text-primary-600" />
                Scoreboard Settings
              </h3>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-800">Leaderboard Visibility</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Show your progress on the public leaderboard
                  </p>
                </div>
                <button
                  onClick={toggleLeaderboardVisibility}
                  disabled={isSubmitting}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    showOnLeaderboard ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      showOnLeaderboard ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                  {isSubmitting && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="h-4 w-4 rounded-full border-2 border-t-transparent border-white animate-spin"></span>
                    </span>
                  )}
                </button>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      <SafeIcon icon={FiUser} className="text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800">Privacy Information</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        When you opt out of the leaderboard, other users won't be able to see your progress or achievements.
                        Your contributions will still count toward your personal statistics and badges.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-100 pt-6">
                <h4 className="font-medium text-gray-800 mb-3">Your Stats</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Points</span>
                      <span className="font-medium">{userStats?.total_points || 0}</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Questions Created</span>
                      <span className="font-medium">{userStats?.questions_created || 0}</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Exams Generated</span>
                      <span className="font-medium">{userStats?.exams_generated || 0}</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Current Streak</span>
                      <span className="font-medium">{userStats?.streak_days || 0} days</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-100 pt-6">
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      <SafeIcon icon={FiCalendar} className="text-yellow-600" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-yellow-800">Streak Information</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Login daily to maintain your streak! Your streak will reset if you miss a day.
                        Longer streaks earn you bonus points with each login.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Scoreboard;